import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  LinearProgress,
  Alert,
  Chip,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import { MdSearch, MdRefresh, MdInfoOutline, MdFileDownload, MdGridOn, MdPrint } from 'react-icons/md';
import { getData } from '../../Webservice/Webservice';
import {
  FONT,
  toISO,
  authToken,
  PRESETS,
  presetRange,
  rangeLabel,
  StatCard,
  statGridSx,
  exportButtonSx,
  downloadCsv,
  downloadExcel,
  openPrintView,
} from './reportShared';

// Config-driven tabbed report page (KPI cards, date presets, filters, tabs, server-side
// pagination, CSV / Excel / print export, notes popup).
//
// tabs:    [{ key, label, defaultSort?, sorts?: [[value,label]], columns, emptyText?, rowAlert?(r) }]
// columns: [{ key, label, numeric?, center?, bold?, text(r, i), cell?(r, i), print?(r), extra?(r), skipExport? }]
// filters: [{ key, label, options: [{value,label}] | (data) => [...], tabs?: [tabKey], defaultValue?, allLabel? }]
// cards(stats, ctx) / banner(stats, ctx) / panel(stats, ctx): ctx = { tabKey, filters, goTo(tabKey, patch), isActive(tabKey, patch) }
const TabbedReport = ({ route, title, subtitle, filePrefix, tabs, filters = [], searchLabel = 'Search', showDates = true, cards, banner, panel }) => {
  const defaults = useMemo(() => Object.fromEntries(filters.map((f) => [f.key, f.defaultValue ?? ''])), [filters]);
  const [tabIndex, setTabIndex] = useState(0);
  const tab = tabs[tabIndex];
  const [preset, setPreset] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [values, setValues] = useState(defaults);
  const [sortBy, setSortBy] = useState(tabs[0].defaultSort || '');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  const visibleFilters = filters.filter((f) => !f.tabs || f.tabs.includes(tab.key));

  const params = useMemo(() => {
    const p = { tab: tab.key };
    if (sortBy) p.sortBy = sortBy;
    if (from) p.from = from;
    if (to) p.to = to;
    if (search) p.search = search;
    for (const f of filters) {
      if ((!f.tabs || f.tabs.includes(tab.key)) && values[f.key] !== '' && values[f.key] !== undefined) p[f.key] = values[f.key];
    }
    return p;
  }, [tab.key, sortBy, from, to, search, values, filters]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getData(route, { ...params, page: page + 1, limit: rowsPerPage }, authToken(), false);
      if (res?.success) setData(res);
      else setError(res?.message || 'Failed to load report');
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [route, params, page, rowsPerPage]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const goTo = (tabKey, patch = {}) => {
    const i = Math.max(tabs.findIndex((t) => t.key === tabKey), 0);
    setTabIndex(i);
    setSortBy(patch.sortBy || tabs[i].defaultSort || '');
    const { sortBy: _ignored, ...rest } = patch;
    setValues({ ...defaults, ...rest });
    setPage(0);
  };
  const isActive = (tabKey, patch = {}) =>
    tab.key === tabKey && Object.entries(patch).every(([k, v]) => (k === 'sortBy' ? sortBy === v : values[k] === v)) && (Object.keys(patch).length > 0 || filters.every((f) => values[f.key] === defaults[f.key]));
  const ctx = { tabKey: tab.key, filters: values, goTo, isActive };

  const applyPreset = (key) => {
    const r = presetRange(key);
    setPreset(key);
    setFrom(r.from);
    setTo(r.to);
    setPage(0);
  };
  const onDateChange = (setter) => (e) => {
    setter(e.target.value);
    setPreset('');
    setPage(0);
  };
  const setValue = (key) => (e) => {
    setValues((v) => ({ ...v, [key]: e.target.value }));
    setPage(0);
  };
  const resetFilters = () => {
    setPreset('all');
    setFrom('');
    setTo('');
    setSearchInput('');
    setSearch('');
    setValues(defaults);
    setSortBy(tab.defaultSort || '');
    setPage(0);
  };

  const exportColumns = tab.columns.filter((c) => !c.skipExport);
  const exportObjects = (rows) =>
    rows.map((r, i) => {
      const obj = {};
      for (const c of exportColumns) {
        obj[c.label] = c.text(r, i + 1);
        if (c.extra) Object.assign(obj, c.extra(r));
      }
      return obj;
    });
  const fileStamp = () => `${filePrefix}_${tab.label.replace(/[^A-Za-z]+/g, '_')}_${from || 'all'}_${to || toISO(new Date())}`;

  const runExport = async (kind) => {
    setExporting(true);
    try {
      const res = await getData(route, { ...params, export: 'all' }, authToken(), false);
      if (!res?.success) throw new Error(res?.message || 'Export failed');
      const rows = res.rows || [];
      if (kind === 'csv') downloadCsv(exportObjects(rows), fileStamp());
      else if (kind === 'excel') downloadExcel(exportObjects(rows), fileStamp(), tab.label);
      else
        openPrintView({
          title: `${title} — ${tab.label}`,
          subtitle: `${showDates ? rangeLabel(from, to) : 'As of today'} · ${rows.length} rows`,
          headers: exportColumns.map((c) => c.label),
          cells: rows.map((r, i) => exportColumns.map((c) => ({ value: c.print ? c.print(r) : c.text(r, i + 1), className: c.numeric ? 'num' : '' }))),
        });
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const stats = data?.stats || {};
  const total = data?.meta?.total || 0;
  const rows = data?.tab === tab.key ? data.rows || [] : [];
  const baseIndex = page * rowsPerPage;
  const bannerNode = banner ? banner(stats, ctx) : null;
  const activeFilterCount = [from || to, search, ...visibleFilters.map((f) => values[f.key] !== defaults[f.key]), sortBy && sortBy !== (tab.defaultSort || '')].filter(Boolean).length;

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 23.5, fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>{title}</Typography>
          {subtitle && <Typography sx={{ fontSize: 12.5, color: '#64748b', fontFamily: FONT }}>{subtitle}</Typography>}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" startIcon={<MdInfoOutline />} onClick={() => setNotesOpen(true)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', fontFamily: FONT }}>Note</Button>
          <Button startIcon={<MdFileDownload />} disabled={exporting || !total} onClick={() => runExport('csv')} sx={exportButtonSx('#16a34a', '#15803d')}>CSV</Button>
          <Button startIcon={<MdGridOn />} disabled={exporting || !total} onClick={() => runExport('excel')} sx={exportButtonSx('#0d9488', '#0f766e')}>Excel</Button>
          <Button startIcon={<MdPrint />} disabled={exporting || !total} onClick={() => runExport('print')} sx={exportButtonSx('#7c3aed', '#6d28d9')}>Print / PDF</Button>
        </Box>
      </Box>

      {cards && (
        <Box sx={statGridSx}>
          {cards(stats, ctx).map((c) => (
            <StatCard key={c.label} {...c} />
          ))}
        </Box>
      )}

      {bannerNode && <Box sx={{ mb: 2 }}>{bannerNode}</Box>}
      {panel && panel(stats, ctx)}

      <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', mb: 3, border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>
            Filters {activeFilterCount > 0 && <Chip size="small" label={`${activeFilterCount} active`} color="primary" sx={{ ml: 1, fontWeight: 700 }} />}
          </Typography>
          <Button size="small" startIcon={<MdRefresh />} onClick={resetFilters} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Reset</Button>
        </Box>
        {showDates && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {PRESETS.map((p) => (
              <Chip
                key={p.key}
                label={p.label}
                onClick={() => applyPreset(p.key)}
                sx={{
                  fontWeight: 700,
                  fontFamily: FONT,
                  background: preset === p.key ? 'rgb(255 209 2)' : '#f1f5f9',
                  color: '#0f172a',
                  border: preset === p.key ? '1px solid #0f172a' : '1px solid transparent',
                  '&:hover': { background: preset === p.key ? 'rgb(255 209 2)' : '#e2e8f0' },
                }}
              />
            ))}
          </Box>
        )}
        <Grid container spacing={2}>
          {showDates && (
            <>
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <TextField label="From" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={from} onChange={onDateChange(setFrom)} inputProps={{ max: to || undefined }} />
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <TextField label="To" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={to} onChange={onDateChange(setTo)} inputProps={{ min: from || undefined }} />
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <TextField
              label={searchLabel}
              size="small"
              fullWidth
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><MdSearch size={18} /></InputAdornment>) }}
            />
          </Grid>
          {visibleFilters.map((f) => {
            const options = typeof f.options === 'function' ? f.options(data) : f.options;
            return (
              <Grid item xs={12} sm={6} md={3} lg={2.5} key={f.key}>
                <TextField select label={f.label} size="small" fullWidth value={values[f.key]} onChange={setValue(f.key)} SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}>
                  {f.allLabel !== null && <MenuItem value="">{f.allLabel || 'All'}</MenuItem>}
                  {(options || []).map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            );
          })}
          {tab.sorts && (
            <Grid item xs={12} sm={6} md={3} lg={2.5}>
              <TextField select label="Sort by" size="small" fullWidth value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(0); }}>
                {tab.sorts.map(([v, l]) => (
                  <MenuItem key={v} value={v}>{l}</MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => goTo(tabs[v].key)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            borderBottom: '1px solid #e2e8f0',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontFamily: FONT, minHeight: 56 },
            '& .Mui-selected': { color: '#0f172a !important' },
            '& .MuiTabs-indicator': { background: 'rgb(255 209 2)', height: 4, borderRadius: 2 },
          }}
        >
          {tabs.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>{tab.label}</Typography>
          <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{total.toLocaleString('en-IN')} rows</Typography>
        </Box>
        {(loading || exporting) && <LinearProgress />}
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {tab.columns.map((c) => (
                  <TableCell key={c.key} align={c.center ? 'center' : c.numeric ? 'right' : 'left'} sx={{ fontWeight: 800, fontFamily: FONT, background: '#0f172a', color: '#fff', whiteSpace: 'nowrap', py: 1.5 }}>
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={tab.columns.length} sx={{ textAlign: 'center', color: '#94a3b8', py: 6, fontFamily: FONT }}>{tab.emptyText || 'Nothing matches the selected filters.'}</TableCell>
                </TableRow>
              )}
              {rows.map((r, idx) => (
                <TableRow key={idx} hover sx={{ '&:nth-of-type(even)': { background: '#f8fafc' }, boxShadow: tab.rowAlert && tab.rowAlert(r) ? 'inset 4px 0 0 #dc2626' : 'none' }}>
                  {tab.columns.map((c) => (
                    <TableCell key={c.key} align={c.center ? 'center' : c.numeric ? 'right' : 'left'} sx={{ fontFamily: FONT, whiteSpace: 'nowrap', fontSize: 11.5, fontWeight: c.bold ? 700 : 500, color: '#334155' }}>
                      {c.cell ? c.cell(r, baseIndex + idx + 1) : c.text(r, baseIndex + idx + 1)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={Math.min(page, Math.max(Math.ceil(total / rowsPerPage) - 1, 0))}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid #e2e8f0' }}
        />
      </Paper>

      <Dialog open={notesOpen} onClose={() => setNotesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontFamily: FONT }}>Note</DialogTitle>
        <DialogContent dividers>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {(data?.notes || []).map((n, i) => (
              <Typography component="li" key={i} variant="body2" sx={{ mb: 1, fontFamily: FONT }}>{n}</Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TabbedReport;

// Small shared cell helpers for config files.
export const pill = (bg, fg) => ({ fontWeight: 700, fontFamily: FONT, background: bg, color: fg, height: 24, fontSize: 11 });
export const Dash = () => <Typography component="span" sx={{ color: '#94a3b8' }}>—</Typography>;
export const TwoLine = ({ top, bottom }) => (
  <Box sx={{ lineHeight: 1.25, minWidth: 140 }}>
    <Typography sx={{ fontSize: 11.5, fontWeight: 700, fontFamily: FONT }}>{top}</Typography>
    {bottom && <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{bottom}</Typography>}
  </Box>
);
export const pctChip = (v, { good = 90, warn = 75, invert = false } = {}) => {
  if (v === null || v === undefined) return <Dash />;
  const ok = invert ? v <= good : v >= good;
  const mid = invert ? v <= warn : v >= warn;
  const [bg, fg] = ok ? ['#dcfce7', '#166534'] : mid ? ['#fef3c7', '#92400e'] : ['#fee2e2', '#b91c1c'];
  return <Chip size="small" label={`${v}%`} sx={pill(bg, fg)} />;
};
export const shareBar = (v, color = 'linear-gradient(90deg,#0ea5e9,#2563eb)') => (
  <Box sx={{ minWidth: 110, display: 'flex', alignItems: 'center', gap: 1 }}>
    <LinearProgress variant="determinate" value={Math.min(v || 0, 100)} sx={{ flex: 1, height: 8, borderRadius: 4, background: '#e2e8f0', '& .MuiLinearProgress-bar': { background: color, borderRadius: 4 } }} />
    <Typography sx={{ fontSize: 11, fontWeight: 700, fontFamily: FONT, minWidth: 42, textAlign: 'right' }}>{v || 0}%</Typography>
  </Box>
);
